/**
 * The honest social-proof line.
 *
 * Renders "You have built N resumes in this browser since <date>" — a number
 * that is true, checkable by the reader, and worth nothing to anyone else. That
 * is the trade this product keeps making: a small true claim over a large
 * unverifiable one.
 *
 * Two constraints shaped the implementation:
 *
 *   1. **It must not shift the layout.** The value lives in localStorage, so it
 *      cannot exist in the prerendered HTML and can only appear after mount. The
 *      wrapper therefore reserves its final height up front — this page measures
 *      CLS 0.000 and a late-arriving line is exactly how that gets lost.
 *   2. **It must say nothing on a first visit.** A visitor with no history sees
 *      an empty reserved space, not "0 resumes", which would be an anti-boast.
 */

import { useEffect, useState } from 'react'
import { Lock } from 'lucide-react'
import { getUsage, type UsageStats } from '@/lib/usageCounter'

const formatSince = (iso: string): string => {
  if (!iso) return ''
  const date = new Date(`${iso}T00:00:00`)
  if (Number.isNaN(date.getTime())) return ''
  return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
}

export const LocalUsage = () => {
  const [stats, setStats] = useState<UsageStats | null>(null)

  // After mount only: localStorage does not exist during the prerender.
  useEffect(() => setStats(getUsage()), [])

  const show = stats && stats.analyses > 0
  const since = show ? formatSince(stats.since) : ''

  return (
    /*
     * min-h reserves the space whether or not the line renders, so appearing
     * after mount moves no other pixel. Two lines on a phone, one from `sm` up —
     * measured, not guessed: a single-line reservation left 0.0052 of shift at
     * 390px because the sentence wraps there. This page holds CLS at 0.000 and
     * that is worth keeping exactly.
     *
     * The values were raised from 10/6 to 15/9 when the line gained a border and
     * vertical padding. Measured, then corrected: 14 (56px) looked right and
     * left 0.0011 of shift at 320-430px, because the bordered line is exactly
     * 60px there. A reservation that is 4px short is the same bug as having
     * none, only harder to notice.
     */
    <div className="flex min-h-15 items-center justify-center sm:min-h-9">
      {show && (
        /*
          Given weight, rather than left as fine print.
          
          This was 12px text at 70% opacity — visually a disclaimer, which is
          the wrong reading. It is the only number on the page that is
          verifiably true, and it says something no competitor can: that the
          count came from this browser and went nowhere. A bordered surface
          lifts it off the ivory canvas so it reads as a stat, and the figure
          itself takes the emerald that the rest of the system reserves for
          emphasis.
        */
        <p className="anim-fade-in inline-flex flex-wrap items-center justify-center gap-x-1.5 gap-y-0.5 rounded-full border border-base-300 bg-base-100 px-3 py-1.5 text-center text-xs">
          <Lock size={12} className="shrink-0 text-primary" aria-hidden="true" />
          <span>
            <strong className="font-semibold text-primary">
              {stats.analyses} resume{stats.analyses === 1 ? '' : 's'}
            </strong>{' '}
            <span className="opacity-75">
              built in this browser
              {since ? ` since ${since}` : ''}
              {stats.exports > 0 ? `, ${stats.exports} downloaded` : ''} — never sent anywhere
            </span>
          </span>
        </p>
      )}
    </div>
  )
}
